import type { Breakpoint } from "@mui/material";
import { Box, Button, Dialog, Fade, Typography, useTheme } from "@mui/material";
import type { PropsWithChildren, ReactNode } from "react";
import React from "react";
import { useNavigate } from "react-router";

type Props = {
  className?: string;
  children: ReactNode;
  isOpen: boolean;
  fullScreen?: boolean;
  handleOpen?: () => void;
  handleClose: () => void;
  label?: string;
  icon?: JSX.Element;
  maxWidth?: Breakpoint | false;
  fullWidth?: boolean;
  showButton?: boolean;
  backgroundColor?: string;
  noBorder?: boolean;
};

export const ModalComponent: React.FC<PropsWithChildren<Props>> = ({
  className,
  children,
  isOpen,
  fullScreen = false,
  handleOpen,
  handleClose,
  label,
  icon,
  showButton = true,
  maxWidth = "md",
  fullWidth = false,
  backgroundColor,
  noBorder = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const IconWrapper = ({ icon, onClick }: { icon: JSX.Element; onClick?: () => void }) =>
    React.cloneElement(icon, {
      style: {
        width: "2.2rem",
        height: "2.2rem",
        borderRadius: "50%",
        padding: "0.5rem",
        backgroundColor: backgroundColor || "#269999",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      },
      onClick,
    });

  const LabelComponent = () => (
    <>
      {label && showButton && (
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "1rem",
            color: theme.palette.text.primary,
            mb: 0.5,
          }}
        >
          {label}
        </Typography>
      )}
      {showButton && handleOpen && (
        <Button onClick={handleOpen} sx={{ p: 0, minWidth: "auto" }}>
          {icon && <IconWrapper icon={icon} />}
        </Button>
      )}
      {!showButton && icon && <IconWrapper onClick={handleOpen} icon={icon} />}
    </>
  );

  return (
    <>
      <LabelComponent />
      <Dialog
        keepMounted={false}
        open={isOpen}
        onClose={() => {
          navigate("/login");
          handleClose();
        }}
        maxWidth={maxWidth}
        fullWidth={fullWidth}
        fullScreen={fullScreen}
        className={className}
        // This removes the dark backdrop overlay:
        BackdropProps={{
          style: { backgroundColor: "" },
        }}
      >
        <Fade in={isOpen} timeout={1000}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 2,
              minWidth: 600,
              width: "80%",
              maxWidth: 1000,
              backgroundColor: "#fff",
            }}
          >
            {children}
          </Box>
        </Fade>
      </Dialog>
    </>
  );
};
